import formatBytes from "core/format/byteFormat";
import {parseApiInstant} from "core/format/dateFormat";
import type {DateTime} from "luxon";
import type {Locale} from "i18n/localePreference";
import {oneOffPurchaseConversionRefusalOf} from "api/apiErrorIds";
import type {Money} from "account/withdrawal/withdrawalRefund";
import type {Schemas} from "api/schemaAssert";
import {isOverLimitPricingRule, type OverLimitPricingRule} from "home/overLimit/OverLimitApi";

/**
 * The buyer's one-off purchases, as the Billing card and the send-form note reason about them
 * (kitecorp/transferify-ui#3004, api kitecorp/transferify-api-service#2196), and the online withdrawal function
 * the card offers for each of them (kitecorp/transferify-ui#3967, api kitecorp/transferify-api-service#3181).
 *
 * Plain TypeScript — this module imports no React — because every rule here is a rule about the reader's
 * money, and the two surfaces that apply it live in different subtrees (the top bar's Billing sheet and
 * the create panel). A rule kept in either component would be re-derived by the other, and this repo has
 * no UI-component tests to catch the two drifting apart.
 *
 * <b>The api decides what a withdrawal does; this module only reads its answer.</b> `withdrawalUntil` is the
 * last instant of the statutory withdrawal period, which the api counts in the buyer's calendar
 * (`StatutoryWithdrawalPeriod`). Nothing here adds fourteen days to anything: a second copy of that rule is how a
 * buyer gets shown a function the api then answers as closed. The only clock read on this side is the check that
 * a cached listing has not aged past its own deadline while the tab sat open.
 */

/**
 * The two purchase families the api sells as one-offs, spelled as the wire spells them. A personalization credit can
 * no longer be spent, because personalization is free for every sender (kitecorp/transferify#525), but credits were
 * sold until late October and the withdrawal function stays open for every one-off inside its period, so the api lists
 * them again and the card shows them. The kind stays a field because the support request names a purchase by kind
 * and id.
 */
export type PurchaseKind = "OVER_LIMIT_ALLOWANCE" | "PERSONALIZATION_CREDIT";

const KINDS: readonly PurchaseKind[] = ["OVER_LIMIT_ALLOWANCE", "PERSONALIZATION_CREDIT"];

/** One paid one-off purchase, as either list of `GET /account/one-off-purchases` carries it. */
export interface OneOffPurchase {
	/** The purchase; for an allowance, what the upload init names to spend it. */
	purchaseId: string;
	kind: PurchaseKind;
	/** What was paid, in `currency`'s minor units. */
	amountMinor: number;
	/** Lowercase ISO-4217. */
	currency: string;
	/** The largest transfer an allowance admits; null for a credit and where the listing stated none. */
	allowanceBytes: number | null;
	/**
	 * The type an allowance was bought for, as the api's enum spells it (kitecorp/transferify-api-service#2217):
	 * the type on which `allowanceBytes` is a floor (kitecorp/transferify-api-service#2787, the owner's rule 9),
	 * which decides what the row says it pays for ({@link allowanceCoverageOf}) and whether a chosen purchase
	 * may stand in for the api's candidate (`home/overLimit/overLimitOffer.preferredOverLimitAllowance`). Null
	 * for a credit, a listing that stated none or a spelling this build does not know, all of which are read as
	 * "no floor known": the row then states only what holds for every allowance, and the composer keeps the api's
	 * candidate.
	 */
	pricing: OverLimitPricingRule | null;
	/** When the payment was confirmed: an ISO instant with its offset. */
	boughtAt: string;
	/**
	 * The last instant (inclusive) of the statutory withdrawal period, as an ISO instant. Absent from an api that
	 * predates the withdrawal function, which {@link rowActionOf} reads as no function to offer for an unused purchase.
	 */
	withdrawalUntil?: string;
}

/** A purchase in `purchases`: paid and not used on a transfer, and so the one kind the holder may turn into credit. */
export interface UnusedPurchase extends OneOffPurchase {
	/**
	 * The api's verdict, from its own clock and its own rules (kitecorp/transferify-api-service#3183, and since
	 * kitecorp/transferify-api-service#3207 only once the statutory withdrawal period has ended): the holder may turn this
	 * purchase into credit toward a plan right now. True only together with {@link convertibleMinor}. This side adds no
	 * date rule of its own: the api is the one clock for it.
	 */
	convertible: boolean;
	/**
	 * The credit the purchase would become, in `currency`'s minor units: what the charge kept, which is the figure the
	 * confirm names and the press sends back as `expectedMinor`, never the price paid. Null exactly when
	 * {@link convertible} is false.
	 */
	convertibleMinor: number | null;
}

/**
 * How a purchase in `withdrawableUsed` was spent, typed from the generated schema so a value the api adds is a compile
 * error in {@link withdrawBodyOf} and not a silent fall-through (kitecorp/transferify-api-service#3210):
 *
 * - `USED`: a transfer used it (a recipient got a file whole, or a hosted upload is complete);
 * - `HELD_UNUSED`: the row reads spent because an upload was admitted against it, but the transfer that holds it was
 *   never finished and used nothing of it. Deleting that transfer hands the purchase back, so it must not be called used.
 */
export type PurchaseUse = Schemas["WithdrawableUsedOneOffPurchase"]["use"];

const USES: readonly PurchaseUse[] = ["USED", "HELD_UNUSED"];

/** A purchase in `withdrawableUsed`: spent on a transfer, and still inside its withdrawal period. */
export interface UsedPurchase extends OneOffPurchase {
	use: PurchaseUse;
}

/**
 * The credit one currency holds on the account's Stripe customer balance: what unused purchases became when they
 * were converted, and what the next plan invoice in that currency takes off (api `ConvertedCredit`). Minor units
 * of `currency`, as the api states them.
 */
export interface ConvertedCredit {
	currency: string;
	amountMinor: number;
}

/**
 * `GET /account/one-off-purchases`: both lists and the converted credit beside them, wrapped so the contract can grow.
 * `credits` is null when the api could not read the balance from Stripe right now, which is not the same as
 * "no credit" (an empty list), so the Billing card shows nothing for it rather than a claim either way.
 */
export interface PurchaseListing {
	purchases: UnusedPurchase[];
	withdrawableUsed: UsedPurchase[];
	credits: readonly ConvertedCredit[] | null;
}

/** No purchases at all: what every payload that is not a listing reads as. */
export const NO_PURCHASES: PurchaseListing = {purchases: [], withdrawableUsed: [], credits: null};

/**
 * The listing off a raw payload, or no purchases for anything that is not one.
 *
 * <b>Every malformed payload answers EMPTY, and the direction is deliberate.</b> The ui ships ahead of the
 * api (kitecorp/transferify-ui#3004 may reach production before kitecorp/transferify-api-service#2196 is
 * deployed), so a 404 body, an html error page, a field renamed on the api side or a session persisted
 * before the endpoint existed must all render NOTHING — not a card with an undefined amount, and never an
 * error surface for a feature the reader was not told about. An entry that fails the shape check is
 * dropped on its own rather than taking the list with it; so is an entry of a kind this ui does not list.
 * A list that is absent, as `withdrawableUsed` is from an api that predates the withdrawal function, is empty.
 */
export function purchaseListingOf(payload: unknown): PurchaseListing {
	if (payload === null || typeof payload !== "object") {
		return NO_PURCHASES;
	}
	const raw = payload as Record<string, unknown>;
	return {
		purchases: purchasesOf(raw.purchases, unusedPurchaseOf),
		withdrawableUsed: purchasesOf(raw.withdrawableUsed, usedPurchaseOf),
		credits: convertedCreditsOf(raw),
	};
}

function purchasesOf<P>(list: unknown, read: (entry: unknown) => P | null): P[] {
	if (!Array.isArray(list)) {
		return [];
	}
	return list.flatMap(entry => {
		const purchase = read(entry);
		return purchase === null ? [] : [purchase];
	});
}

/**
 * The converted credit off a raw listing payload: one entry per currency in the api's order, or null when the
 * payload does not state a credit list at all.
 *
 * Null and empty are kept apart so the listing keeps the api's own distinction (an empty array is "nothing
 * converted is unspent", null is "the balance could not be read right now"); `useUnusedPurchases` folds both
 * into an empty list for the card. A payload without a `credits` array (an api from before the field, an html error
 * page) is null too. An entry whose currency is not a non-empty string, or whose amount is
 * not a finite number above zero, is dropped on its own: a card line "0,00 lei" or "NaN" would claim a credit the
 * reader does not hold.
 */
export function convertedCreditsOf(payload: unknown): ConvertedCredit[] | null {
	if (payload === null || typeof payload !== "object") {
		return null;
	}
	const list = (payload as Record<string, unknown>).credits;
	if (!Array.isArray(list)) {
		return null;
	}
	return list.flatMap(entry => {
		const credit = convertedCreditOf(entry);
		return credit === null ? [] : [credit];
	});
}

function convertedCreditOf(entry: unknown): ConvertedCredit | null {
	if (entry === null || typeof entry !== "object") {
		return null;
	}
	const raw = entry as Record<string, unknown>;
	if (typeof raw.currency !== "string" || raw.currency === "") return null;
	if (typeof raw.amountMinor !== "number" || !Number.isFinite(raw.amountMinor) || raw.amountMinor <= 0) return null;
	return {currency: raw.currency, amountMinor: raw.amountMinor};
}

/** An entry of `purchases`: a purchase of either list, plus the api's conversion verdict on it. */
function unusedPurchaseOf(entry: unknown): UnusedPurchase | null {
	const purchase = purchaseOf(entry);
	if (purchase === null) {
		return null;
	}
	const raw = entry as Record<string, unknown>;
	// The choice is offered only with the figure it names: a verdict without a credit worth stating would open a
	// confirm that cannot say what it asks the buyer to give up, and a press the api would refuse (`expectedMinor`).
	const convertibleMinor = raw.convertible === true
		&& typeof raw.convertibleMinor === "number" && Number.isFinite(raw.convertibleMinor) && raw.convertibleMinor > 0
		? raw.convertibleMinor : null;
	return {...purchase, convertible: convertibleMinor !== null, convertibleMinor};
}

/** An entry of `withdrawableUsed`: a purchase of either list, plus how it was spent. */
function usedPurchaseOf(entry: unknown): UsedPurchase | null {
	const purchase = purchaseOf(entry);
	if (purchase === null) {
		return null;
	}
	const {use} = entry as Record<string, unknown>;
	// An absent or unknown use keeps the row, because dropping it would take the withdrawal function from a buyer the
	// api lists, and reads as USED: the wording the card had before the api stated the difference.
	return {...purchase, use: USES.find(known => known === use) ?? "USED"};
}

function purchaseOf(entry: unknown): OneOffPurchase | null {
	if (entry === null || typeof entry !== "object") {
		return null;
	}
	const raw = entry as Record<string, unknown>;
	const kind = raw.kind;
	if (typeof raw.purchaseId !== "string" || raw.purchaseId === "") return null;
	if (typeof kind !== "string" || !KINDS.includes(kind as PurchaseKind)) return null;
	if (typeof raw.amountMinor !== "number" || !Number.isFinite(raw.amountMinor)) return null;
	if (typeof raw.currency !== "string" || raw.currency === "") return null;
	if (typeof raw.boughtAt !== "string") return null;
	const allowanceBytes = typeof raw.allowanceBytes === "number" && Number.isFinite(raw.allowanceBytes)
		? raw.allowanceBytes : null;
	// Lenient in the same direction as the size: a rule this build does not know reads as "none stated",
	// which claims no floor, so it can never lead to an init the api would refuse.
	const pricing = isOverLimitPricingRule(raw.pricing) ? raw.pricing : null;
	return {
		purchaseId: raw.purchaseId,
		kind: kind as PurchaseKind,
		amountMinor: raw.amountMinor,
		currency: raw.currency,
		allowanceBytes,
		pricing,
		boughtAt: raw.boughtAt,
		// Left out, not invented, when the api does not state one.
		...(typeof raw.withdrawalUntil === "string" ? {withdrawalUntil: raw.withdrawalUntil} : {}),
	};
}

/**
 * The size an allowance admits, as the card and the note state it: "8.53 GB" for the size a quote was
 * actually taken at, "20 GB" for a round one. Two decimals are what the quoted figure needs to be told
 * apart from a neighbouring size; a whole number of gigabytes carries none rather than a trailing ".00"
 * nothing decided. Gigabytes, not megabytes: an allowance is never below the free cap, so the formatter's
 * rung is GB or above, and 8.53 GB IS a whole number of megabytes. Same formatter as every other size on
 * the site, so the Romanian page reads "8,53 GB".
 */
export function allowanceSizeLabel(allowanceBytes: number, locale: Locale): string {
	return formatBytes(allowanceBytes, locale, allowanceBytes % 1_000_000_000 === 0 ? 0 : 2);
}

/**
 * What an allowance's row says it pays for (kitecorp/transferify-api-service#2787, the owner's rules 5 and 9 of
 * 2026-10-17; the api's `docs/pricing/over-limit-one-off.md`, "When a purchase is used"): one transfer of either
 * type, worth what was paid, and never less than the size it was quoted on the type it was bought for. A larger
 * transfer than that needs a plan: the row says so (kitecorp/transferify-ui#3839, copy D5) and offers no
 * difference to pay, because the api no longer sells a one-off to top a purchase up.
 *
 * - `hostedUpTo`: a HOSTED purchase, whose quoted size is that floor, and which pays for an instant send of any
 *   size too, because an instant send costs the least hosted price at every size;
 * - `worthWhatWasPaid`: everything else. An instant send is one flat price at every size, so an instant purchase's
 *   quoted size limits nothing, and the hosted size its money buys is the api's to compute from the live catalogue,
 *   which the listing does not carry. A listing that named no type or no size gets this too: it claims no floor.
 *
 */
export type AllowanceCoverage = {kind: "hostedUpTo"; size: string} | {kind: "worthWhatWasPaid"};

export function allowanceCoverageOf(purchase: UnusedPurchase, locale: Locale): AllowanceCoverage {
	return purchase.pricing === "HOSTED" && purchase.allowanceBytes !== null
		? {kind: "hostedUpTo", size: allowanceSizeLabel(purchase.allowanceBytes, locale)}
		: {kind: "worthWhatWasPaid"};
}

/**
 * The catalog's sentence for a coverage, picked here once for the three places that state it (the Billing card's
 * row, the send-form note and the strip after "Use it"), so they cannot say two different things about one purchase.
 */
export function coverageSentence(
	coverage: AllowanceCoverage,
	covers: {overLimit: string; overLimitHosted: (size: string) => string},
): string {
	return coverage.kind === "hostedUpTo" ? covers.overLimitHosted(coverage.size) : covers.overLimit;
}

/**
 * What a row offers.
 *
 * - `withdraw`: the row shows the online withdrawal function (art. 11^1 OUG 34/2014, § 356a BGB);
 * - `closed`: the period is over, or the api stated none. An allowance then stays usable and says so; a credit has
 *   nothing left to offer ({@link isShownOnCard}).
 */
export type RowAction = "withdraw" | "closed";

/**
 * Which of the two a row offers.
 *
 * <b>An unused purchase is judged by its deadline against `now`.</b> The api lists an unused purchase whatever its age
 * (an allowance stays usable for good) and states only the deadline, so this clock is the one judgment there is. The
 * listing is cached and revalidated, so a function offered this morning would otherwise outlive its deadline in a tab
 * left open and keep promising a refund the api no longer makes by itself: a declaration received after the period is
 * acknowledged and left to a person. An unreadable or absent deadline is treated as passed, for the same reason.
 *
 * <b>A used purchase is judged by the api's listing.</b> The api lists `withdrawableUsed` only while the period runs
 * (`OneOffPurchaseService.unusedFor`), so being in it is the api's own answer, and a device clock a few hours fast must
 * not take the function away from the buyer on the last evening ("available throughout the period", art. 11^1 alin. (2),
 * § 356a Abs. 1 BGB). A stale cache is replaced when the sheet is next opened.
 *
 * @param spent true for a purchase of the `withdrawableUsed` list
 */
export function rowActionOf(purchase: OneOffPurchase, now: Date, spent: boolean): RowAction {
	if (spent) {
		return "withdraw";
	}
	const closesAt = parseApiInstant(purchase.withdrawalUntil);
	return closesAt !== null && closesAt.toMillis() >= now.getTime() ? "withdraw" : "closed";
}

/**
 * The statutory last day, as a calendar date in UTC, or null where the deadline is absent or unreadable.
 *
 * The api closes the period at London midnight, so its instant falls at 23:00 or 24:00 UTC of the last day. Written
 * on a Romanian reader's clock that instant is 01:59 or 02:59 of the NEXT day, and "until 12 Nov" would promise the
 * whole of a day the right does not cover. The UTC date is the last day itself in both seasons, so the card states it.
 */
export function withdrawalLastDayOf(purchase: OneOffPurchase): DateTime | null {
	return parseApiInstant(purchase.withdrawalUntil);
}

/**
 * Whether the card lists a purchase: when it still offers something. An unused allowance can always be used, so it
 * stays listed past its period; every other purchase is listed only while the withdrawal function is open for it
 * ({@link rowActionOf}), so the card never shows a row with no action (a credit can be spent on nothing).
 *
 * @param spent true for a purchase of the `withdrawableUsed` list
 */
export function isShownOnCard(purchase: OneOffPurchase, spent: boolean, now: Date): boolean {
	if (!spent && purchase.kind === "OVER_LIMIT_ALLOWANCE") {
		return true;
	}
	return rowActionOf(purchase, now, spent) === "withdraw";
}

/** Whether a row's purchase came from `withdrawableUsed`, the one list whose entries state how they were spent. */
export function isUsedPurchase(purchase: UnusedPurchase | UsedPurchase): purchase is UsedPurchase {
	return "use" in purchase;
}

/** The confirm texts a row can state: one per way a purchase can stand when the buyer withdraws from it. */
export interface WithdrawBodies {
	/** An unused purchase: the api refunds the whole charge and revokes it. */
	bodyUnused: (price: string) => string;
	/** A used purchase: a person decides what is owed. */
	bodyUsed: (email: string) => string;
	/** A purchase an unfinished transfer holds: a person decides, and deleting that transfer refunds it automatically. */
	bodyHeld: (email: string) => string;
}

/**
 * The text of a row's withdrawal confirm, picked here once so the card holds no rule about it. A held purchase must
 * never be told it was used: it was not, and its buyer can still get the automatic refund by deleting the transfer.
 */
export function withdrawBodyOf(
	purchase: UnusedPurchase | UsedPurchase, text: WithdrawBodies, facts: {price: string; email: string},
): string {
	if (!isUsedPurchase(purchase)) {
		return text.bodyUnused(facts.price);
	}
	switch (purchase.use) {
		case "USED":
			return text.bodyUsed(facts.email);
		case "HELD_UNUSED":
			return text.bodyHeld(facts.email);
	}
}

/** The catalog's label for a purchase's family, which the row, the declaration and the receipt all name it by. */
export function kindLabelOf(
	purchase: OneOffPurchase, labels: {overLimit: string; personalization: string},
): string {
	return purchase.kind === "OVER_LIMIT_ALLOWANCE" ? labels.overLimit : labels.personalization;
}

/**
 * The purchases a transfer can be sent against: the allowances. The send-form note and "Use it" read this, never the
 * whole list, because a credit listed for its withdrawal has no use to offer.
 */
export function spendablePurchasesOf(purchases: readonly UnusedPurchase[]): UnusedPurchase[] {
	return purchases.filter(purchase => purchase.kind === "OVER_LIMIT_ALLOWANCE");
}

/** The listing without one purchase, from whichever list holds it; the same object where it holds none. */
export function listingWithout(listing: PurchaseListing, purchaseId: string): PurchaseListing {
	const keep = (purchase: OneOffPurchase) => purchase.purchaseId !== purchaseId;
	const purchases = listing.purchases.filter(keep);
	const withdrawableUsed = listing.withdrawableUsed.filter(keep);
	return purchases.length === listing.purchases.length && withdrawableUsed.length === listing.withdrawableUsed.length
		? listing
		: {...listing, purchases, withdrawableUsed};
}

/**
 * What one press of "turn it into credit toward a plan" came to, as the row states it
 * (`OneOffPurchaseService.ConvertOneOffPurchase`, kitecorp/transferify-ui#3839, copy D6).
 *
 * - `converted`: the purchase is credit now;
 * - `alreadyUsed`, `alreadyRefunded`: the purchase is no longer the buyer's to convert, and leaves the list;
 * - `tooEarly`: the purchase's statutory withdrawal period is still running (kitecorp/transferify-api-service#3207);
 * - `unavailable`: the conversion is not open to this purchase, and support helps;
 * - `amountChanged`: the credit the buyer was shown is no longer what the api would make; the listing is re-read and
 *   the confirm names the new figure before a second press;
 * - `failed`: every other refusal and a transport failure. After a 502 the purchase can be half-converted and gone from
 *   the listing, so the line never says nothing changed, and the repeat press is safe: the api answers the same conversion.
 */
export type ConvertOutcome =
	| "converted"
	| "alreadyUsed"
	| "alreadyRefunded"
	| "tooEarly"
	| "unavailable"
	| "amountChanged"
	| "failed";

/** Classifies a rejected conversion by the api's stable `ErrorInfo` reason, never by its localized sentence. */
export function convertOutcomeOf(error: unknown): Exclude<ConvertOutcome, "converted"> {
	return oneOffPurchaseConversionRefusalOf(error) ?? "failed";
}

/** One conversion press, settled: the outcome, and on success the credit the api states it made. */
export interface ConvertResult {
	outcome: ConvertOutcome;
	credited: Money | null;
}

/** Whether a settled attempt is a conversion rather than a withdrawal, which decides the sentence the row states. */
export function isConvertResult<R extends object>(result: R | ConvertResult): result is ConvertResult {
	return "credited" in result;
}

/**
 * Whether the purchase is no longer the holder's after this conversion outcome, and so must leave the shared list at
 * once rather than on the next background refresh: the send-form note reads the same list and must not keep offering a
 * purchase the buyer has just converted or been told was spent or refunded.
 */
export function conversionLeavesList(outcome: ConvertOutcome): boolean {
	return outcome === "converted" || outcome === "alreadyUsed" || outcome === "alreadyRefunded";
}

/**
 * Which of D5's closed-period lines a row shows (kitecorp/transferify-ui#3839): the one that ends "or turn it into
 * credit toward a plan" only where the api lists the purchase as convertible, so no line offers a choice the row has no
 * button for. The other row keeps the line that was there before the choice existed.
 */
export function closedWindowLineOf(purchase: UnusedPurchase): "withChoice" | "noChoice" {
	return purchase.convertible ? "withChoice" : "noChoice";
}

/** The purchase "Use it" was pressed for, if it is still unused; null once it is spent or refunded. */
export function usingPurchaseOf(
	purchases: readonly UnusedPurchase[], purchaseId: string | null,
): UnusedPurchase | null {
	if (purchaseId === null) {
		return null;
	}
	return purchases.find(purchase => purchase.purchaseId === purchaseId) ?? null;
}

/**
 * Whether the send-form note shows (kitecorp/transferify-ui#3004, dismissal rule chosen by default on
 * 2026-09-27 pending the owner's word).
 *
 * The note is CLOSABLE, and closing it records the purchases the reader had in front of them at the time.
 * It stays closed, across reloads, for exactly those purchases — and comes back the moment a purchase
 * they had NOT seen appears, because that is news. `seenAtDismissal` null means never dismissed on this
 * browser for this account. While a purchase is being USED the strip takes the slot instead, so the note
 * yields to it.
 */
export function unusedPurchaseNoteVisible(
	purchases: readonly UnusedPurchase[],
	seenAtDismissal: readonly string[] | null,
	usingPurchaseId: string | null,
): boolean {
	if (purchases.length === 0 || usingPurchaseId !== null) {
		return false;
	}
	if (seenAtDismissal === null) {
		return true;
	}
	return purchases.some(purchase => !seenAtDismissal.includes(purchase.purchaseId));
}
